import {
  GetEventClockSeconds,
  GetEventClockTime,
  GetEventStatusPeriodLengthMinutes,
  IsValidEventClockTime,
  SECONDS_IN_MINUTE,
} from '../../configuration'

export type RugbyClockStatusId = number

export type RugbyClockTiming = {
  periodLength: number
  extraPeriodLength: number
  extraTimePlayed: boolean
}

type RugbyClockTimingFn = (timing: RugbyClockTiming) => number

type RugbyClockTimingMap = {
  fromMatchStart: RugbyClockTimingFn
  periodLength: RugbyClockTimingFn
  afterFirstHalf: RugbyClockTimingFn
  afterExtraFirstHalf: RugbyClockTimingFn
  afterExtraTime: RugbyClockTimingFn
  penaltyPhaseEnd: RugbyClockTimingFn
}

export const rugbyClockTiming: RugbyClockTimingMap = {
  fromMatchStart: (_timing: RugbyClockTiming) => 0,
  periodLength: (timing: RugbyClockTiming) => timing.periodLength,
  afterFirstHalf: (timing: RugbyClockTiming) => timing.periodLength * 2,
  afterExtraFirstHalf: (timing: RugbyClockTiming) =>
    timing.periodLength * 2 + timing.extraPeriodLength,
  afterExtraTime: (timing: RugbyClockTiming) =>
    timing.periodLength * 2 + timing.extraPeriodLength * 2,
  penaltyPhaseEnd: (timing: RugbyClockTiming) =>
    timing.extraTimePlayed
      ? rugbyClockTiming.afterExtraTime(timing)
      : rugbyClockTiming.afterFirstHalf(timing),
}

export type RugbyClockResolution =
  | { kind: 'fixed'; matchMinutes: number }
  | {
      kind: 'running'
      startOffsetMinutes: number
      periodCapMinutes: number
    }

export type RugbyStatusClockRule = (
  timing: RugbyClockTiming,
) => RugbyClockResolution

export const rugbyClockFixed = (
  minutes: RugbyClockTimingFn,
): RugbyStatusClockRule => {
  return (timing) => ({ kind: 'fixed', matchMinutes: minutes(timing) })
}

export const rugbyClockRunning = (
  startOffsetMinutes: RugbyClockTimingFn,
  periodCapMinutes: RugbyClockTimingFn,
): RugbyStatusClockRule => {
  return (timing) => ({
    kind: 'running',
    startOffsetMinutes: startOffsetMinutes(timing),
    periodCapMinutes: periodCapMinutes(timing),
  })
}

const RUGBY_DEFAULT_CLOCK_RESOLUTION: RugbyClockResolution = {
  kind: 'running',
  startOffsetMinutes: 0,
  periodCapMinutes: 0,
}

function toTiming(
  periodLength: number,
  extraPeriodLength: number,
  extraTimePlayed: boolean,
): RugbyClockTiming {
  return { periodLength, extraPeriodLength, extraTimePlayed }
}

/** Maps stored `clock_time` to seconds within the current running period. */
function periodLocalSecondsFromStoredClockTime(
  clockTime: number,
  startOffsetMinutes: number,
  periodLength: number,
): number {
  if (startOffsetMinutes <= 0) return clockTime

  const startOffsetSeconds = startOffsetMinutes * SECONDS_IN_MINUTE
  const regularTimeEndSeconds = periodLength * 2 * SECONDS_IN_MINUTE

  if (clockTime < startOffsetSeconds) return clockTime

  const matchSecondsIfRelative = startOffsetSeconds + clockTime

  if (
    matchSecondsIfRelative > regularTimeEndSeconds &&
    clockTime < regularTimeEndSeconds
  )
    return clockTime

  if (clockTime >= startOffsetSeconds && clockTime <= regularTimeEndSeconds)
    return clockTime - startOffsetSeconds

  return clockTime
}

function matchSecondsFromStoredClockTime(
  clockTime: number,
  startOffsetMinutes: number,
  periodLength: number,
): number {
  return (
    startOffsetMinutes * SECONDS_IN_MINUTE +
    periodLocalSecondsFromStoredClockTime(
      clockTime,
      startOffsetMinutes,
      periodLength,
    )
  )
}

export const createRugbyEventClockConfig = (
  statusClock: Partial<Record<RugbyClockStatusId, RugbyStatusClockRule>>,
) => {
  const resolve = (statusId: RugbyClockStatusId, timing: RugbyClockTiming) => {
    const rule = statusClock[statusId]
    return rule ? rule(timing) : RUGBY_DEFAULT_CLOCK_RESOLUTION
  }

  const getEventStatusPeriodLengthMinutes: GetEventStatusPeriodLengthMinutes = (
    eventStatusId,
    periodLength,
    extraPeriodLength,
    extraTimePlayed,
  ) => {
    const timing = toTiming(periodLength, extraPeriodLength, extraTimePlayed)
    const resolved = resolve(eventStatusId as RugbyClockStatusId, timing)

    if (resolved.kind === 'fixed') return resolved.matchMinutes

    return resolved.periodCapMinutes
  }

  const getEventPeriodStartOffsetMinutes: GetEventStatusPeriodLengthMinutes = (
    eventStatusId,
    periodLength,
    extraPeriodLength,
    extraTimePlayed,
  ) => {
    const timing = toTiming(periodLength, extraPeriodLength, extraTimePlayed)
    const resolved = resolve(eventStatusId as RugbyClockStatusId, timing)

    if (resolved.kind === 'fixed') return resolved.matchMinutes

    return resolved.startOffsetMinutes
  }

  const getEventClockSeconds: GetEventClockSeconds = (
    clockTime,
    periodLengthMinutes,
    periodLength,
    extraPeriodLength,
    _,
    eventStatusId,
    extraTimePlayed = false,
  ) => {
    const timing = toTiming(periodLength, extraPeriodLength, extraTimePlayed)
    const resolved = resolve(eventStatusId as RugbyClockStatusId, timing)

    if (resolved.kind === 'fixed')
      return (periodLengthMinutes ?? resolved.matchMinutes) * SECONDS_IN_MINUTE

    return matchSecondsFromStoredClockTime(
      clockTime,
      resolved.startOffsetMinutes,
      periodLength,
    )
  }

  const isValidEventClockTime: IsValidEventClockTime = () => true

  const getEventClockTime: GetEventClockTime = (
    minute,
    second,
    periodStartOffsetMinutes,
    _isTimerDirectionDown,
  ) => {
    const matchSeconds = minute * SECONDS_IN_MINUTE + second
    const offsetSeconds = (periodStartOffsetMinutes ?? 0) * SECONDS_IN_MINUTE
    const storedSegmentSeconds = matchSeconds - offsetSeconds

    if (offsetSeconds > 0 && storedSegmentSeconds === offsetSeconds)
      return matchSeconds

    return storedSegmentSeconds
  }

  return {
    getEventStatusPeriodLengthMinutes,
    getEventPeriodStartOffsetMinutes,
    getEventClockSeconds,
    isValidEventClockTime,
    getEventClockTime,
  }
}
